import {
  DataRetentionPolicyCategoryDto,
  DataRetentionPolicyDetailCategory,
  RetentionOptions,
} from "../../../../common/service/server/dto/DataRetentionPolicyDto";
import { CategoryDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { DATA_RETENTION_CATEGORIES, descriptionForTypeAndPeriod } from "./dataRetentionCategories";

// The guided Data Retention flow: an intro statement, one question per built-in purpose (in the
// order the policy lists them), then a catch-all step for custom purposes.

export const INTRO_SLUG = "retention-policy";
// Survey answer that records the intro statement was approved (data-retention-survey).
export const INTRO_APPROVED_SLUG = "retention-intro-approved";

export const INTRO_STEP_KEY = "intro";
export const CUSTOM_STEP_KEY = "custom";
export const NEW_PURPOSE_KEY = "new-purpose";

export type GuidingQuestion = { before: string; highlight: string; after: string };

type BuiltinStep = {
  title: string;
  question: GuidingQuestion;
};

export const BUILTIN_STEPS: Record<DataRetentionPolicyDetailCategory, BuiltinStep> = {
  ONLINE_DATA_FROM_WEBSITE: {
    title: "Website use",
    question: {
      before: "When someone ",
      highlight: "uses your website",
      after: ", what data do you collect about them?",
    },
  },
  PROCESS_AND_SHIP_ORDERS: {
    title: "Orders and shipping",
    question: {
      before: "When you ",
      highlight: "process and ship orders",
      after: ", what data do you collect?",
    },
  },
  CONTACT_CUSTOMER_SUPPORT: {
    title: "Customer support",
    question: {
      before: "When a customer ",
      highlight: "contacts you for support",
      after: " or with other questions, what data do you collect?",
    },
  },
  SIGN_UP_FOR_PROMOTIONAL_AND_MARKETING: {
    title: "Marketing communications",
    question: {
      before: "When someone ",
      highlight: "signs up for marketing",
      after: " emails or texts, what data do you collect?",
    },
  },
  REVIEW_PRODUCTS_ANSWER_SURVEYS_SEND_FEEDBACK: {
    title: "Reviews, surveys and feedback",
    question: {
      before: "When someone ",
      highlight: "reviews a product, answers a survey or sends feedback",
      after: ", what data do you collect?",
    },
  },
  PRIVACY_REQUESTS: {
    title: "Privacy requests",
    question: {
      before: "When someone ",
      highlight: "makes a privacy request",
      after: ", what data do you collect?",
    },
  },
  SECURITY_PURPOSES: {
    title: "Security",
    question: {
      before: "What data do you keep for ",
      highlight: "security",
      after: ", like preventing fraud or protecting accounts?",
    },
  },
};

export const INTRO_QUESTION: GuidingQuestion = {
  before: "We’ll start with an ",
  highlight: "intro statement",
  after: " for how long you keep data.",
};

export const CUSTOM_QUESTION: GuidingQuestion = {
  before: "Do you keep data for ",
  highlight: "any other purpose",
  after: "?",
};

export const BUILTIN_ORDER: DataRetentionPolicyDetailCategory[] = (
  Object.keys(DATA_RETENTION_CATEGORIES) as DataRetentionPolicyDetailCategory[]
).sort(
  (a, b) => DATA_RETENTION_CATEGORIES[a].displayOrder - DATA_RETENTION_CATEGORIES[b].displayOrder,
);

export const PERIOD_OPTIONS: { value: RetentionOptions; label: string }[] = [
  { value: "YEARS_2", label: "2 years" },
  { value: "YEARS_5", label: "5 years" },
  { value: "YEARS_7", label: "7 years" },
  { value: "YEARS_10", label: "10 years" },
  { value: "INDEFINITE", label: "As long as needed" },
  { value: "CUSTOM", label: "Write your own" },
];

const DEFAULT_PERIOD: RetentionOptions = "YEARS_7";

export type PurposeDraft = {
  pic: string[];
  period: RetentionOptions;
  // The statement when `period` is CUSTOM (and always for purposes without a period choice).
  customText: string;
  // Only custom purposes have an editable label.
  label: string;
};

export const canChoosePeriod = (type: DataRetentionPolicyDetailCategory | undefined | null) =>
  type == null || DATA_RETENTION_CATEGORIES[type].description.includes("YEARS");

const YEARS_TEXT: Partial<Record<RetentionOptions, string>> = {
  YEARS_2: "2 years",
  YEARS_5: "5 years",
  YEARS_7: "7 years",
  YEARS_10: "10 years",
};

// Custom purposes have no built-in sentence, so the period choices write this one.
export const customPurposeDescription = (period: RetentionOptions): string | null => {
  if (period === "INDEFINITE") {
    return "We keep this data as long as necessary to fulfill the purpose for which we collect it.";
  }
  const years = YEARS_TEXT[period];
  return years ? `We keep this data for up to ${years}, then delete or anonymize it.` : null;
};

const sentenceFor = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  period: RetentionOptions,
): string | null =>
  type ? descriptionForTypeAndPeriod(type, period) : customPurposeDescription(period);

const normalize = (text: string) => text.replace(/\s+/g, " ").trim();

// Older answers were saved as CUSTOM even when the text is one of the generated sentences; map
// those back to their period so the choice shows as selected.
const matchPeriod = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  description: string,
): RetentionOptions | null => {
  const target = normalize(description);
  if (!target) {
    return null;
  }
  const match = PERIOD_OPTIONS.find(
    ({ value }) => value !== "CUSTOM" && normalize(sentenceFor(type, value) ?? "") === target,
  );
  return match?.value ?? null;
};

export const draftDescription = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  draft: PurposeDraft,
): string => {
  if (draft.period === "CUSTOM") {
    return draft.customText.trim();
  }
  return sentenceFor(type, draft.period) ?? draft.customText.trim();
};

// Switching a purpose to "Write your own" starts from the sentence that was showing, so it can be
// tweaked, unless the purpose already holds the user's own words (typed earlier, or saved as custom
// text), which come back as they were left. A saved generated sentence is not their own words.
export const startingCustomText = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  draft: PurposeDraft,
): string => {
  const hasOwnText = draft.customText.trim() !== "" && matchPeriod(type, draft.customText) == null;
  return hasOwnText ? draft.customText : draftDescription(type, draft);
};

export const suggestedPicIds = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  collectedPic: CategoryDto[],
): string[] => {
  if (!type) {
    return [];
  }
  const keys = DATA_RETENTION_CATEGORIES[type].suggested;
  return collectedPic.filter((pic) => keys.includes(pic.key)).map((pic) => pic.id);
};

export const initialDraft = (
  detail: DataRetentionPolicyCategoryDto | null,
  collectedPic: CategoryDto[],
): PurposeDraft => {
  const type = detail?.builtinType ?? null;
  const collectedIds = new Set(collectedPic.map((pic) => pic.id));
  const savedPic = (detail?.pic ?? []).filter((id) => collectedIds.has(id));
  const untouched = !detail || (!detail.approved && savedPic.length === 0 && !detail.description);
  const pic = savedPic.length === 0 && untouched ? suggestedPicIds(type, collectedPic) : savedPic;
  const description = detail?.description ?? "";
  const label = detail?.customLabel ?? "";

  if (type && !canChoosePeriod(type)) {
    return {
      pic,
      period: "CUSTOM",
      customText: description || DATA_RETENTION_CATEGORIES[type].description,
      label,
    };
  }

  let period: RetentionOptions;
  if (untouched || (!detail?.retentionPeriod && !description)) {
    period = DEFAULT_PERIOD;
  } else if (!detail?.retentionPeriod || detail.retentionPeriod === "CUSTOM") {
    period = matchPeriod(type, description) ?? "CUSTOM";
  } else {
    period = detail.retentionPeriod;
  }

  return { pic, period, customText: description, label };
};

export const isDraftComplete = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  draft: PurposeDraft,
): boolean =>
  draft.pic.length > 0 &&
  draftDescription(type, draft).length > 0 &&
  (type != null || draft.label.trim().length > 0);

export const isDetailComplete = (detail: DataRetentionPolicyCategoryDto): boolean =>
  detail.approved && detail.pic.length > 0 && Boolean(detail.description?.trim());

// The privacy policy's "Type of Data" cell (PrivacyPolicyService.getDataRetentionDetailRows).
export const policyRowLabel = (
  type: DataRetentionPolicyDetailCategory | undefined | null,
  customLabel: string,
  picNames: string[],
): string => {
  const label = type ? DATA_RETENTION_CATEGORIES[type].label : customLabel.trim() || "Other data";
  return picNames.length > 0 ? `${label}, including ${picNames.join(", ")}` : label;
};

export const picNamesInOrder = (ids: string[], collectedPic: CategoryDto[]): string[] => {
  const selected = new Set(ids);
  return collectedPic.filter((pic) => selected.has(pic.id)).map((pic) => pic.name);
};

export const TOTAL_STEPS = BUILTIN_ORDER.length + 2;

export type RetentionProgress = {
  // The first step still waiting for an answer; the custom step once every earlier one is done.
  activeKey: string;
  // 1-based position of `activeKey`.
  activeNumber: number;
  isIntroDone: boolean;
  isComplete: boolean;
};

// Shared by the builder (which step is open) and the page footer (what to do next).
export const retentionProgress = (
  details: DataRetentionPolicyCategoryDto[],
  answers: Record<string, string | undefined>,
  unmappedCount: number,
): RetentionProgress => {
  // Any approved purpose means the intro was already passed (or the org predates the steps).
  const isIntroDone = answers[INTRO_APPROVED_SLUG] === "true" || details.some((d) => d.approved);
  const firstOpen = BUILTIN_ORDER.findIndex((type) => {
    const detail = details.find((d) => d.builtinType === type);
    // A built-in purpose with no row was marked as not applicable.
    return detail != null && !isDetailComplete(detail);
  });

  if (!isIntroDone) {
    return { activeKey: INTRO_STEP_KEY, activeNumber: 1, isIntroDone, isComplete: false };
  }
  if (firstOpen >= 0) {
    return {
      activeKey: BUILTIN_ORDER[firstOpen],
      activeNumber: firstOpen + 2,
      isIntroDone,
      isComplete: false,
    };
  }
  const customsDone = details.filter((d) => !d.builtinType).every(isDetailComplete);
  return {
    activeKey: CUSTOM_STEP_KEY,
    activeNumber: TOTAL_STEPS,
    isIntroDone,
    isComplete: customsDone && unmappedCount === 0,
  };
};
